import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, of, takeUntil, Subject } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { Project } from 'src/app/interfaces/api/project';
import { PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';
import { KydDuplicateNameDialogComponent } from '../kyd-duplicate-name-dialog/kyd-duplicate-name-dialog.component';

// Returned via dialogRef.close() so the caller (kyd.component.ts) can offer to configure
// whatever was actually created right away - a KYD created from here has no settings at all
// yet (the site itself can't reach out over Bluetooth to read/write anything).
export interface KydBulkAddResult {
  created: Tracker[];
}

// One row per device, name/serial only - the same two fields resources.component.ts's
// TrackersModalComponent asks for. `type: 'KYD'` is set directly at creation: unlike other
// device types, a KYD has no shared/reusable config to pick afterward that would otherwise
// set it (see tracker-config.tool.ts), so there is nothing else that would ever set it here.
interface KydRow {
  name: string;
  serial: string;
}

@Component({
  selector: 'app-kyd-bulk-add-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    MatToolbar,
    MatFormFieldModule,
    MatInputModule,
    ButtonComponent,
  ],
  standalone: true,
  templateUrl: './kyd-bulk-add-dialog.component.html',
  styleUrls: ['./kyd-bulk-add-dialog.component.scss']
})
export class KydBulkAddDialogComponent implements OnInit, OnDestroy {
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly data: { currentProject?: Project } = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<KydBulkAddDialogComponent, KydBulkAddResult | false> = inject(MatDialogRef<KydBulkAddDialogComponent, KydBulkAddResult | false>);

  private readonly destroy$ = new Subject<void>();

  rows: KydRow[] = [{ name: '', serial: '' }];
  saving: boolean = false;

  // Fetched once so save() can check typed names against every existing tracker (not just KYD -
  // the backend's own uniqueness check isn't scoped by type either) before ever submitting.
  private existingTrackers: Tracker[] = [];

  ngOnInit() {
    this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
      this.existingTrackers = trackers || [];
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addRow() {
    this.rows.push({ name: '', serial: '' });
  }

  removeRow(index: number) {
    if (this.rows.length <= 1) return;
    this.rows.splice(index, 1);
  }

  // "KYD" is what identifies a tracker as one everywhere in the app (isKydTracker() on both
  // sides), and every row created from this dialog is explicitly type: 'KYD' regardless - so
  // rather than block the save like a rename does (kyd-config-dialog.component.ts's own
  // confirmRename()), a name typed without it here just gets it appended automatically.
  private ensureKydInName(name: string): string {
    return name.toUpperCase().includes('KYD') ? name : `${name} KYD`;
  }

  save() {
    this.rows.forEach(row => {
      if (row.name?.trim()) row.name = this.ensureKydInName(row.name.trim());
    });

    const validRows = this.rows.filter(r => r.name?.trim());
    if (!validRows.length) return;

    const duplicates = validRows
      .map(row => this.existingTrackers.find(t => t.name === row.name.trim()))
      .filter((t): t is Tracker => !!t);

    if (!duplicates.length) {
      this.performSave(validRows);
      return;
    }

    const dialogRef = this.dialog.open(KydDuplicateNameDialogComponent, {
      width: '450px',
      data: { names: duplicates.map(t => t.name) },
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe(proceed => {
      if (proceed) this.performSave(validRows);
    });
  }

  // Names already caught by the duplicate-name dialog above are updated in place (the user
  // explicitly agreed the existing KYD's values can be replaced) instead of being POSTed as a
  // second Tracker under the same name - see kyd-duplicate-name-dialog.component.ts.
  private performSave(validRows: KydRow[]) {
    this.saving = true;
    const creations = validRows.map(row => {
      const name = row.name.trim();
      const serial = row.serial?.trim() || undefined;
      const existing = this.existingTrackers.find(t => t.name === name);

      const request = existing
        ? this.trackerService.setTracker({ ...existing, serial, type: 'KYD' } as PutTracker)
        : this.trackerService.addTracker({
          name,
          serial,
          type: 'KYD',
          project: this.data.currentProject?.id,
        });

      return request.pipe(
        // PUT /trackers/:id (setTracker) does NOT return the updated Tracker - it responds
        // with one {message} per project it touched (see api/project/trackers.js). Reading
        // that response's non-existent `.id` downstream (promptConfigureNewKyds() -> the
        // config dialog -> another PUT) sent a second request to "/trackers/undefined" and
        // 400'd. `existing` is already the up-to-date Tracker (with a real id) for this row.
        map((response: any) => ({ success: true as const, err: null as any, tracker: (existing ? { ...existing, serial, type: 'KYD' } : response) as Tracker })),
        catchError(err => of({ success: false as const, err, tracker: null as any }))
      );
    });

    forkJoin(creations).subscribe(results => {
      this.saving = false;
      const failures = results.filter(r => !r.success);
      const created = results.filter(r => r.success).map(r => r.tracker as Tracker);

      if (failures.length === 0) {
        this.pop.toast(this.translate.instant('trackers.add_success'), 'success', 4000, 'add');
        this.dialogRef.close({ created });
      } else if (failures.length < results.length) {
        this.pop.warning(this.translate.instant('trackers.error.some_kyd_add_failed', { failed: failures.length, total: results.length }));
        this.dialogRef.close({ created });
      } else {
        this.pop.error(this.describeError(failures[0]?.err));
      }
    });
  }

  // The backend's own duplicate check ("Tracker already exist", api/project/trackers.js) is
  // only ever a fallback here (the pre-check in save() catches virtually every case) - it's
  // raw, untranslated English text, so it's mapped to a translated message rather than shown
  // as-is when it does surface (e.g. a name created by someone else between the check and now).
  private describeError(err: any): string {
    const raw: string = err?.error?.message || err?.error?.error || err?.error?.toString() || (err?.toString ? err.toString() : err);
    if (typeof raw === 'string' && raw.toLowerCase().includes('already exist')) {
      return this.translate.instant('trackers.tracker_already_exists');
    }
    return raw;
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
